import React, { useState } from 'react';
import { Target, Calendar, DollarSign, ArrowRight, ShieldCheck, CheckCircle2, Sparkles, HelpCircle } from 'lucide-react';
import { WealthCalculationInputs, ContributionTiming } from '../../types';
import { calculateGoalSolver } from '../../lib/wealthMathEngine';

export const GoalPlannerSolverView: React.FC = () => {
  const [goalInputs, setGoalInputs] = useState<WealthCalculationInputs>({
    initialPrincipal: 5000,
    periodicContribution: 400,
    nominalReturnRatePct: 6.0,
    inflationRatePct: 2.5,
    annualFeePct: 0.20,
    timeHorizonYears: 5,
    compoundingFrequency: 'monthly',
    contributionFrequency: 'monthly',
    contributionTiming: 'END_OF_PERIOD',
    targetGoalAmount: 35000, // e.g. Home Deposit
    currency: 'USD',
  });

  const [goalType, setGoalType] = useState<'HOME' | 'EMERGENCY' | 'EDUCATION' | 'VEHICLE' | 'CUSTOM'>('HOME');

  const result = calculateGoalSolver(goalInputs);

  const goalPresets = {
    HOME: { title: 'Home Deposit & Closing Costs', target: 50000, years: 5, initial: 10000 },
    EMERGENCY: { title: '6-Month Emergency Safety Fund', target: 18000, years: 2, initial: 3000 },
    EDUCATION: { title: 'College Education Reserve', target: 80000, years: 10, initial: 5000 },
    VEHICLE: { title: 'Electric Vehicle Purchase', target: 25000, years: 3, initial: 4000 },
    CUSTOM: { title: 'Custom Financial Milestone', target: 100000, years: 10, initial: 10000 },
  };

  const selectGoalPreset = (key: keyof typeof goalPresets) => {
    setGoalType(key);
    const p = goalPresets[key];
    setGoalInputs((prev) => ({
      ...prev,
      targetGoalAmount: p.target,
      timeHorizonYears: p.years,
      initialPrincipal: p.initial,
    }));
  };

  const progressPct = Math.min(
    100,
    Math.round(((result.nominalFutureValue) / (goalInputs.targetGoalAmount || 1)) * 100)
  );

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-16">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 rounded-2xl p-6 sm:p-8 text-white shadow-xl">
        <div className="space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 text-xs font-semibold border border-blue-500/30">
            <Target className="w-3.5 h-3.5" />
            <span>YamooPay Goal Solver Engine</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            Financial Goal Planner & Required Savings Solver
          </h1>
          <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
            Determine exact monthly contributions required to reach any target amount, calculate projected completion dates, and solve timeline gaps deterministically.
          </p>
        </div>
      </div>

      {/* Preset Selector Chips */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-bold text-slate-700 mr-2">Goal Categories:</span>
        {(Object.keys(goalPresets) as (keyof typeof goalPresets)[]).map((key) => (
          <button
            key={key}
            onClick={() => selectGoalPreset(key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer border ${
              goalType === key
                ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
            }`}
          >
            {goalPresets[key].title}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Controls Column */}
        <div className="lg:col-span-5 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-6">
          <h2 className="text-base font-bold text-slate-900 border-b border-slate-100 pb-3">Goal Parameters</h2>

          <div className="space-y-4 text-xs text-slate-700">
            <div>
              <label className="block font-semibold text-slate-900 mb-1">Target Amount Needed ($)</label>
              <input
                type="number"
                value={goalInputs.targetGoalAmount}
                onChange={(e) =>
                  setGoalInputs((prev) => ({ ...prev, targetGoalAmount: parseFloat(e.target.value) || 0 }))
                }
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-bold text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-semibold text-slate-900 mb-1">Starting Savings ($)</label>
                <input
                  type="number"
                  value={goalInputs.initialPrincipal}
                  onChange={(e) =>
                    setGoalInputs((prev) => ({ ...prev, initialPrincipal: parseFloat(e.target.value) || 0 }))
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block font-semibold text-slate-900 mb-1">Time Horizon (Years)</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={goalInputs.timeHorizonYears}
                  onChange={(e) =>
                    setGoalInputs((prev) => ({ ...prev, timeHorizonYears: parseInt(e.target.value) || 1 }))
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-semibold text-slate-900 mb-1">Current Monthly Savings ($)</label>
                <input
                  type="number"
                  value={goalInputs.periodicContribution}
                  onChange={(e) =>
                    setGoalInputs((prev) => ({ ...prev, periodicContribution: parseFloat(e.target.value) || 0 }))
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block font-semibold text-slate-900 mb-1">Assumed Return (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={goalInputs.nominalReturnRatePct}
                  onChange={(e) =>
                    setGoalInputs((prev) => ({ ...prev, nominalReturnRatePct: parseFloat(e.target.value) || 0 }))
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Solver Output Column */}
        <div className="lg:col-span-7 space-y-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-6">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div>
                <div className="text-xs text-slate-500 font-medium">Solver Outcome</div>
                <h3 className="text-lg font-bold text-slate-900">Required Monthly Contribution</h3>
              </div>
              <div className="text-right">
                <div className="text-2xl sm:text-3xl font-extrabold text-blue-600">
                  ${result.monthlySavingsRequired?.toLocaleString()} / mo
                </div>
                <div className="text-xs text-slate-500">To reach target in {goalInputs.timeHorizonYears} years</div>
              </div>
            </div>

            {/* Target Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-xs font-semibold text-slate-700">
                <span>Current Monthly Contribution: ${goalInputs.periodicContribution}/mo</span>
                <span className={goalInputs.periodicContribution >= (result.monthlySavingsRequired || 0) ? 'text-emerald-600 font-bold' : 'text-amber-600 font-bold'}>
                  {goalInputs.periodicContribution >= (result.monthlySavingsRequired || 0) ? 'On Track for Target!' : `Monthly Gap: +$${Math.round((result.monthlySavingsRequired || 0) - goalInputs.periodicContribution)}/mo needed`}
                </span>
              </div>
              <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                <div
                  style={{ width: `${progressPct}%` }}
                  className="bg-blue-600 h-full transition-all"
                />
              </div>
              <div className="flex justify-between text-[11px] text-slate-500">
                <span>Projected Value: ${result.nominalFutureValue.toLocaleString()}</span>
                <span>Target: ${goalInputs.targetGoalAmount?.toLocaleString()}</span>
              </div>
            </div>

            {/* Timeline Solver Output */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2 text-xs">
              <div className="font-semibold text-slate-900 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-600" />
                <span>Estimated Time to Reach Target at Current Contribution (${goalInputs.periodicContribution}/mo):</span>
              </div>
              <div className="text-sm font-bold text-slate-900">
                {result.timeToGoalYears} years
              </div>
              <div className="text-slate-600">
                Based on your initial principal of ${goalInputs.initialPrincipal.toLocaleString()} and assumed annual return of {goalInputs.nominalReturnRatePct}%.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
